import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  productsService,
  Product,
  ProductFilters,
  ProductModule,
  Lesson,
} from '@/services/products.service';

// Backwards compatibility - export Course as alias for Product
export type Course = Product;
export type CourseFilters = ProductFilters;
export type CourseModule = ProductModule;
export type { Lesson, Product };

export const courseKeys = {
  all: ['courses'] as const,
  lists: () => [...courseKeys.all, 'list'] as const,
  list: (filters: ProductFilters) => [...courseKeys.lists(), filters] as const,
  details: () => [...courseKeys.all, 'detail'] as const,
  detail: (id: string) => [...courseKeys.details(), id] as const,
  slug: (slug: string) => [...courseKeys.all, 'slug', slug] as const,
  modules: (productId: string) =>
    [...courseKeys.all, productId, 'modules'] as const,
  lesson: (lessonId: string) => ['lesson', lessonId] as const,
};

// Also export as productKeys for new code
export const productKeys = courseKeys;

// Queries
export function useCourses(filters: ProductFilters = {}) {
  return useQuery({
    queryKey: courseKeys.list(filters),
    queryFn: () => productsService.findAll(filters),
  });
}

// Alias for new naming
export const useProducts = useCourses;

export function useCourse(id: string, enabled = true) {
  return useQuery({
    queryKey: courseKeys.detail(id),
    queryFn: () => productsService.findById(id),
    enabled: enabled && !!id,
  });
}

export const useProduct = useCourse;

export function useCourseBySlug(slug: string, enabled = true) {
  return useQuery({
    queryKey: courseKeys.slug(slug),
    queryFn: () => productsService.findBySlug(slug),
    enabled: enabled && !!slug,
  });
}

export const useProductBySlug = useCourseBySlug;

export function useCourseModules(productId: string, enabled = true) {
  return useQuery({
    queryKey: courseKeys.modules(productId),
    queryFn: () => productsService.getModules(productId),
    enabled: enabled && !!productId,
  });
}

export const useProductModules = useCourseModules;

export function useLesson(lessonId: string, enabled = true) {
  return useQuery({
    queryKey: courseKeys.lesson(lessonId),
    queryFn: () => productsService.getLessonWithProgress(lessonId),
    enabled: enabled && !!lessonId,
  });
}

// Mutations
export function useCreateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (product: Partial<Product>) => productsService.create(product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

export const useCreateProduct = useCreateCourse;

export function useUpdateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...product }: Partial<Product> & { id: string }) =>
      productsService.update(id, product),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(data.id) });
      if (data.slug) {
        queryClient.invalidateQueries({ queryKey: courseKeys.slug(data.slug) });
      }
    },
  });
}

export const useUpdateProduct = useUpdateCourse;

export function useDeleteCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

export const useDeleteProduct = useDeleteCourse;

export function usePublishCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.publish(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(data.id) });
    },
  });
}

export const usePublishProduct = usePublishCourse;

export function useArchiveCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.archive(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(data.id) });
    },
  });
}

export const useArchiveProduct = useArchiveCourse;

export function useUnarchiveCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.unarchive(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(data.id) });
    },
  });
}

export function useBulkPublishCourses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids?: string[]) => productsService.publishBulk(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

export const useBulkPublishProducts = useBulkPublishCourses;

export function useMergeToCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { sourceIds: string[]; targetId: string }) =>
      productsService.mergeToCourse(params.sourceIds, params.targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

// Module mutations
export function useCreateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      productId,
      ...module
    }: Partial<ProductModule> & { courseId?: string; productId?: string }) =>
      productsService.createModule(productId || courseId!, module),
    onSuccess: (_, variables) => {
      const id = variables.productId || variables.courseId!;
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(id) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(id) });
    },
  });
}

export function useUpdateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      moduleId,
      courseId: _courseId,
      productId: _productId,
      ...module
    }: Partial<ProductModule> & {
      moduleId: string;
      courseId?: string;
      productId?: string;
    }) => productsService.updateModule(moduleId, module),
    onSuccess: (_, variables) => {
      const id = variables.productId || variables.courseId!;
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(id) });
    },
  });
}

export function useDeleteModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      moduleId: string;
      courseId?: string;
      productId?: string;
    }) => productsService.deleteModule(variables.moduleId),
    onSuccess: (_, variables) => {
      const id = variables.productId || variables.courseId!;
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(id) });
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(id) });
    },
  });
}

export function useReorderModules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      productId,
      moduleIds,
    }: {
      courseId?: string;
      productId?: string;
      moduleIds: string[];
    }) => productsService.reorderModules(productId || courseId!, moduleIds),
    onSuccess: (_, variables) => {
      const id = variables.productId || variables.courseId!;
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(id) });
    },
  });
}

// Lesson mutations
export function useCreateLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      moduleId,
      courseId: _courseId,
      productId: _productId,
      ...lesson
    }: Partial<Lesson> & {
      moduleId: string;
      courseId?: string;
      productId?: string;
    }) => productsService.createLesson(moduleId, lesson),
    onSuccess: (_, variables) => {
      const id = variables.productId || variables.courseId!;
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(id) });
    },
  });
}

export function useUpdateLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lessonId,
      courseId: _courseId,
      productId: _productId,
      ...lesson
    }: Partial<Lesson> & {
      lessonId: string;
      courseId?: string;
      productId?: string;
    }) => productsService.updateLesson(lessonId, lesson),
    onSuccess: (_, variables) => {
      const id = variables.productId || variables.courseId!;
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(id) });
      queryClient.invalidateQueries({
        queryKey: courseKeys.lesson(variables.lessonId),
      });
    },
  });
}

export function useDeleteLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      lessonId: string;
      courseId?: string;
      productId?: string;
    }) => productsService.deleteLesson(variables.lessonId),
    onSuccess: (_, variables) => {
      const id = variables.productId || variables.courseId!;
      queryClient.invalidateQueries({ queryKey: courseKeys.modules(id) });
    },
  });
}

// ===== Many-to-Many hooks =====

export const moduleKeys = {
  all: ['modules'] as const,
  list: (search?: string) => [...moduleKeys.all, 'list', search] as const,
};

export const lessonKeys = {
  all: ['lessons'] as const,
  list: (search?: string) => [...lessonKeys.all, 'list', search] as const,
};

export function useAllModules(search?: string) {
  return useQuery({
    queryKey: moduleKeys.list(search),
    queryFn: () => productsService.getAllModules(search),
  });
}

export function useAllLessons(search?: string) {
  return useQuery({
    queryKey: lessonKeys.list(search),
    queryFn: () => productsService.getAllLessons(search),
  });
}

export function useLinkModuleToProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      moduleId,
      productId,
    }: {
      moduleId: string;
      productId: string;
    }) => productsService.linkModuleToProduct(moduleId, productId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: courseKeys.detail(variables.productId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.modules(variables.productId),
      });
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

export function useUnlinkModuleFromProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      moduleId,
      productId,
    }: {
      moduleId: string;
      productId: string;
    }) => productsService.unlinkModuleFromProduct(moduleId, productId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: courseKeys.detail(variables.productId),
      });
      queryClient.invalidateQueries({
        queryKey: courseKeys.modules(variables.productId),
      });
      queryClient.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

export function useLinkLessonToModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lessonId,
      moduleId,
    }: {
      lessonId: string;
      moduleId: string;
      productId?: string;
    }) => productsService.linkLessonToModule(lessonId, moduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}

export function useUnlinkLessonFromModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lessonId,
      moduleId,
    }: {
      lessonId: string;
      moduleId: string;
      productId?: string;
    }) => productsService.unlinkLessonFromModule(lessonId, moduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}

export function useConvertToModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      targetProductId,
    }: {
      productId: string;
      targetProductId?: string;
    }) => productsService.convertToModule(productId, targetProductId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
    },
  });
}
